/* global React, Icon, Button, Select, useState, t */
// ============================================================
// AVSTEMMINGSMAPPA — periodevalg, fagområdemeny og topplinje
// ============================================================
// Skilt fra avstemming-workspace.jsx, som eier tilstanden og kallene mot
// backend. Her ligger rammen rundt: hvilken periode man ser på, hvilke
// fagområder som finnes, og hva toppen sier om hva som gjenstår.
//
// Menyen og toppen oppfører seg forskjellig i kontrollmodus — se
// avstemming-kontroll.jsx for hvorfor.

const avstDefaultPeriod = () => {
  const now = new Date();
  const month = now.getMonth() - 1;
  const year = month < 0 ? now.getFullYear() - 1 : now.getFullYear();
  const index = month < 0 ? 11 : month;
  return `${year}-${String(index + 1).padStart(2, '0')}`;
};

const avstPeriodOptions = () => {
  const options = [];
  const now = new Date();
  for (let back = 1; back <= 12; back += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
    options.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: window.PlummeI18n.date(date, { month: 'long', year: 'numeric' }),
    });
  }
  return options;
};

const avstPeriodLabel = (periodId) => {
  const [year, month] = String(periodId || '').split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return Number.isNaN(date.getTime()) ? periodId : window.PlummeI18n.date(date, { month: 'long', year: 'numeric' });
};

// The chat message to the client is always written in Norwegian, so its month name must not follow the UI language.
const avstPeriodLabelNb = (periodId) => {
  const [year, month] = String(periodId || '').split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return Number.isNaN(date.getTime()) ? periodId : date.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });
};

function AvstAreaNavItem({ area, accounts, areaStatuses, view, onSelect, collapsed, dimmed }) {
  const status = areaStatuses?.[area.key] || 'ikke_kontrollert';
  const counts = window.avstAreaCounts(accounts, area);
  const extra = window.avstAreaNavExtra(status, counts);
  return <button
    type="button"
    className={'kav-nav-item' + (view === area.key ? ' active' : '') + (dimmed ? ' kav-nav-dim' : '')}
    onClick={() => onSelect(area.key)}
    title={collapsed ? t('avcs.nav.areaTitle', { area: area.displayLabel || area.label, extra }) : (dimmed ? t('avcs.nav.contextOnly') : '')}
  >
    <Icon name={area.icon} size={16} />
    {collapsed ? null : <span className="grow">
      {area.displayLabel || area.label}
      <span className={'kav-nav-extra kav-tag-' + window.avstmCell(status).cls}>{extra}</span>
    </span>}
  </button>;
}

// Venstremenyen. «Oppsummering» er ikke et fagområde og har ingen status — den
// er inngangen, og står derfor øverst uten telling.
// I kontrollmodus gjelder kontrollen ofte ETT omraade. Da staar det omraadet
// alene, og resten samles nedtonet under «Ovrige omraader» — de er periodens
// kontekst, ikke det som skal kontrolleres. Vises alt likt, mister kontrolloren
// av syne hva hen faktisk gaar god for.
function AvstAreaNav({ areas, accounts, areaStatuses, view, onSelect, collapsed, onToggleCollapse, scopedAreaKey }) {
  const [othersOpen, setOthersOpen] = useState(false);
  const primary = scopedAreaKey ? areas.filter((area) => area.key === scopedAreaKey) : areas;
  const others = scopedAreaKey ? areas.filter((area) => area.key !== scopedAreaKey) : [];
  return <nav className={'kav-nav' + (collapsed ? ' collapsed' : '')}>
    <div className="kav-nav-items">
      <button
        type="button"
        className={'kav-nav-item' + (view === 'oppsummering' ? ' active' : '')}
        onClick={() => onSelect('oppsummering')}
        title={collapsed ? t('avcs.nav.summary') : ''}
      >
        <Icon name="sliders" size={16} />
        {collapsed ? null : <span>{t('avcs.nav.summary')}</span>}
      </button>
      {primary.map((area) => <AvstAreaNavItem
        key={area.key} area={area} accounts={accounts} areaStatuses={areaStatuses}
        view={view} onSelect={onSelect} collapsed={collapsed}
      />)}
      {others.length ? <React.Fragment>
        <button type="button" className="kav-nav-item kav-nav-group-toggle" onClick={() => setOthersOpen((value) => !value)}
          title={collapsed ? t('avcs.nav.otherAreas', { count: others.length }) : ''}>
          <Icon name={othersOpen ? 'chevDown' : 'chevRight'} size={14} />
          {collapsed ? null : <span className="grow">{t('avcs.nav.otherAreas', { count: others.length })}</span>}
        </button>
        {othersOpen && !collapsed ? <React.Fragment>
          <div className="kav-nav-group-note">{t('avcs.nav.otherAreasNote')}</div>
          {others.map((area) => <AvstAreaNavItem
            key={area.key} area={area} accounts={accounts} areaStatuses={areaStatuses}
            view={view} onSelect={onSelect} collapsed={collapsed} dimmed
          />)}
        </React.Fragment> : null}
      </React.Fragment> : null}
    </div>
    <button type="button" className="kav-nav-collapse" onClick={onToggleCollapse} title={collapsed ? t('avcs.nav.showMenu') : t('avcs.nav.hideMenu')}>
      <Icon name={collapsed ? 'chevRight' : 'chevLeft'} size={14} />
      {collapsed ? null : <span>{t('avcs.nav.hideMenu')}</span>}
    </button>
  </nav>;
}

// Toppen sier hva som gjenstår før perioden kan avsluttes: avvik, manglende
// dokumentasjon, åpne forespørsler og fremdrift. Tallene er de samme som
// oppsummeringen og backend regner med — ikke en egen opptelling.
function AvstWorkspaceHeader({
  businessName, organizationNumber, periodId, setPeriodId, progress, avvik, mangler, openRequests, responsible,
  locked, busy, kontrollMode, control, onBack, onToggleLock, onSubmitForControl, onRequest, onExport,
}) {
  return <div className="kav-header">
    {onBack ? <button type="button" className="kav-back" onClick={onBack}>
      <Icon name="chevLeft" size={16} />
      {kontrollMode ? t('avcs.header.backToReviewInbox') : t('rf.avst.allClients', 'Alle klienter')}
    </button> : null}
    {kontrollMode && window.AvstKontrollBanner
      ? <window.AvstKontrollBanner control={control} avvik={avvik} mangler={mangler} progress={progress} />
      : null}
    <div className="kav-header-row">
      <div className="kav-header-id">
        <div className="rf-title" style={{ fontSize: 19 }}>{businessName}</div>
        <div className="t-sm muted" style={{ fontWeight: 700 }}>
          {organizationNumber ? t('avcs.matrix.org', { org: organizationNumber }) + ' · ' : ''}{avstPeriodLabel(periodId)}
        </div>
      </div>
      <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Select value={periodId} onChange={(event) => setPeriodId(event.target.value)} aria-label={t('avcs.common.period')}>
          {avstPeriodOptions().map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
        {/* Handlingene i toppen er utførerens. Kontrolløren har sine i panelet,
            og skal ikke kunne endre arbeidet hen skal gå god for. */}
        {kontrollMode ? <Button size="sm" variant="secondary" icon="download" onClick={onExport}>{t('avcs.header.exportReviewBasis')}</Button>
          : <React.Fragment>
            <Button size="sm" variant="secondary" icon="mail" disabled={busy} onClick={onRequest}>{t('avcs.common.requestDocs')}</Button>
            <Button size="sm" variant="secondary" icon="download" onClick={onExport}>{t('avcs.header.export')}</Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={onSubmitForControl}>{t('avcs.header.submitForReview')}</Button>
            {/* «Fullfør periode» ER låsen. Teksten under sier hva det innebærer, og
                gjenåpning krever en begrunnelse som blir stående i loggen. */}
            <Button size="sm" icon={locked ? 'refresh' : 'lock'} disabled={busy} onClick={onToggleLock}>
              {locked ? t('avcs.header.reopenPeriod') : t('avcs.header.completePeriod')}
            </Button>
          </React.Fragment>}
      </div>
    </div>
    <div className="kav-header-mini">
      <span className={avvik ? 'kav-warn-text' : ''}>{t('avcs.common.deviations', { count: avvik })}</span><span className="dotsep" />
      <span className={mangler ? 'kav-warn-text' : ''}>{t('avcs.common.docsMissing', { count: mangler })}</span><span className="dotsep" />
      <span>{t('avcs.header.openRequests', { count: openRequests })}</span>
      {responsible ? <React.Fragment><span className="dotsep" /><span>{t('avcs.header.responsible', { name: responsible })}</span></React.Fragment> : null}
    </div>
    <div className="avst-progress" style={{ width: 210, marginTop: 10 }}>
      <div className="bar"><span style={{ width: progress + '%', background: 'var(--plum-600)' }} /></div>
      <span className="avst-progress-n">{progress}%</span>
    </div>
  </div>;
}

// `control` gjor mappa til kontrollflate: utforerens arbeid blir
// skrivebeskyttet, navigasjonen scopes til omraadet kontrollen gjelder, og
// kontrollorens egne vurderinger ligger i panelet til hoyre. Samme mappe, andre
// oyne — se avstemming-kontroll.jsx.

Object.assign(window, {
  AvstAreaNav, AvstAreaNavItem, AvstWorkspaceHeader,
  avstDefaultPeriod, avstPeriodLabel, avstPeriodLabelNb, avstPeriodOptions,
});
